import { useState, useEffect, useMemo } from 'react';
import { computeSpineMatrix, type SpineMatrixRow } from '@/lib/spineMatrix';
import type { FrontComponentKind } from '@/lib/spine';
import { useBowProfiles } from '../profiles/hooks';
import type { BowProfile } from '../profiles/types';

interface ArrowInputs {
  arrowLengthIn: number;
  pointGr: number;
  insertGr: number;
  /** Front-component kind — drives the v140 stiffening model. */
  frontComponent: FrontComponentKind;
  selectedSpine: number;
}

const DEFAULTS: ArrowInputs = {
  arrowLengthIn: 28.5,
  pointGr: 100,
  insertGr: 50,
  frontComponent: 'standard',
  selectedSpine: 340,
};

export function SpineWhatIfPage() {
  const { profiles, primary } = useBowProfiles();
  const [selectedBowId, setSelectedBowId] = useState<string | ''>('');
  const [bowType, setBowType] = useState<'compound' | 'recurve' | 'traditional'>('compound');
  const [iboSpeed, setIboSpeed] = useState<number>(340);
  const [letOff, setLetOff] = useState<number>(80);
  const [drawWeight, setDrawWeight] = useState<number>(70);
  const [drawLength, setDrawLength] = useState<number>(29);
  const [arrow, setArrow] = useState<ArrowInputs>(DEFAULTS);

  /* Auto-load primary bow on mount if present */
  useEffect(() => {
    if (primary && !selectedBowId) {
      applyBow(primary);
      setSelectedBowId(primary.profileId);
    }
  }, [primary]);

  function applyBow(p: BowProfile) {
    setDrawWeight(p.bow.drawWeightLb);
    setDrawLength(p.bow.drawLengthIn);
    setIboSpeed(p.bow.iboFps);
  }

  function handleBowSelect(profileId: string) {
    setSelectedBowId(profileId);
    const p = profiles.find((bp) => bp.profileId === profileId);
    if (p) applyBow(p);
  }

  const matrix = useMemo(() => {
    /* CTI / extra front-mass weight is rolled into insertGr for the spine math
     * because the spine algorithm cares about total mass forward of the
     * shaft midpoint, not where it lives along the front. */
    return computeSpineMatrix({
      drawWeight,
      bowType,
      iboSpeed,
      letOff,
      arrowLengthIn: arrow.arrowLengthIn,
      pointGr: arrow.pointGr,
      insertGr: arrow.insertGr,
      frontComponent: arrow.frontComponent,
    });
  }, [drawWeight, bowType, iboSpeed, letOff, arrow]);

  const inRange = matrix.filter((r) => r.verdict === 'on' || r.verdict === 'slightly-stiff' || r.verdict === 'slightly-weak');
  const idealRow = matrix.find((r) => r.verdict === 'on') ?? inRange[0];
  const headerRange = matrix[0] ? `${matrix[0].recommendedMin}–${matrix[0].recommendedMax}` : '';
  const effDw = matrix[0]?.effectiveDrawWeight ?? 0;

  return (
    <div className="spine-page">
      <nav className="forge-nav">
        <a href="https://sparrowexpeditions.com/pages/the-forge" target="_top">The Forge</a>
        <a href="/wind-drift.html">Wind-Drift</a>
        <a href="/build-review.html">Build Review</a>
        <a href="/tune.html">Tune Wizard</a>
        <a href="/range.html">Range Log</a>
        <a href="/bows.html">My Bows</a>
        <a href="/arrows.html">My Arrows</a>
        <a href="/spine.html" className="active">Spine What-If</a>
        <a href="/pages/methods#ashby" target="_blank" rel="noopener">Methods</a>
      </nav>

      <header className="spine-header">
        <h1>Spine What-If</h1>
        <p className="lede">
          Find your in-window arrow spine. The matrix cycles through every standard
          spine (250, 300, 340, 400, 500, 600, 700, 800, 900, 1000) at your exact bow
          + arrow specs and shows which lands in range.
        </p>
      </header>

      <section className="spine-inputs">
        <div className="input-block">
          <h3>Bow</h3>
          {profiles.length > 0 && (
            <label>
              Load from saved
              <select value={selectedBowId} onChange={(e) => handleBowSelect(e.target.value)}>
                <option value="">— manual entry —</option>
                {profiles.map((p) => (
                  <option key={p.profileId} value={p.profileId}>
                    {p.name} {p.isPrimary ? '(primary)' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="input-grid">
            <label>
              Bow type
              <select value={bowType} onChange={(e) => setBowType(e.target.value as typeof bowType)}>
                <option value="compound">Compound</option>
                <option value="recurve">Recurve</option>
                <option value="traditional">Traditional</option>
              </select>
            </label>
            <label>
              Draw weight (lb)
              <input
                type="number"
                value={drawWeight}
                step={1}
                min={20}
                max={100}
                onChange={(e) => setDrawWeight(Number(e.target.value))}
              />
            </label>
            <label>
              Draw length (in)
              <input
                type="number"
                value={drawLength}
                step={0.25}
                min={22}
                max={32}
                onChange={(e) => setDrawLength(Number(e.target.value))}
              />
            </label>
            {bowType === 'compound' && (
              <>
                <label>
                  IBO fps
                  <input
                    type="number"
                    value={iboSpeed}
                    step={1}
                    min={280}
                    max={370}
                    onChange={(e) => setIboSpeed(Number(e.target.value))}
                  />
                </label>
                <label>
                  Letoff (%)
                  <input
                    type="number"
                    value={letOff}
                    step={1}
                    min={60}
                    max={90}
                    onChange={(e) => setLetOff(Number(e.target.value))}
                  />
                </label>
              </>
            )}
          </div>
        </div>

        <div className="input-block">
          <h3>Arrow</h3>
          <div className="input-grid">
            <label>
              Cut length (in)
              <input
                type="number"
                value={arrow.arrowLengthIn}
                step={0.125}
                min={20}
                max={34}
                onChange={(e) => setArrow({ ...arrow, arrowLengthIn: Number(e.target.value) })}
              />
            </label>
            <label>
              Point (gr)
              <input
                type="number"
                value={arrow.pointGr}
                step={25}
                min={75}
                max={300}
                onChange={(e) => setArrow({ ...arrow, pointGr: Number(e.target.value) })}
              />
            </label>
            <label>
              Insert + collar (gr)
              <input
                type="number"
                value={arrow.insertGr}
                step={5}
                min={0}
                max={150}
                onChange={(e) => setArrow({ ...arrow, insertGr: Number(e.target.value) })}
              />
            </label>
            <label className="span-2">
              Extended insert / CTI
              <select
                value={arrow.frontComponent}
                onChange={(e) => setArrow({ ...arrow, frontComponent: e.target.value as FrontComponentKind })}
                title="Models partial-shaft stiffening + added mass per manufacturer-published spec"
              >
                <option value="standard">Standard HIT / half-out (no stiffening, mass only)</option>
                <option value="iron-will-snyder">Iron Will Snyder Core — 3" embed</option>
                <option value="firenock-cti-3">Firenock CTI 3"</option>
                <option value="firenock-cti-6">Firenock CTI 6" (~46gr)</option>
                <option value="evolution-glue-in">Evolution Outdoors glue-in</option>
                <option value="easton-dg">Easton Deep Glue-in (DG)</option>
              </select>
            </label>
            <label>
              Current spine
              <input
                type="number"
                value={arrow.selectedSpine}
                step={10}
                min={150}
                max={1000}
                onChange={(e) => setArrow({ ...arrow, selectedSpine: Number(e.target.value) })}
              />
            </label>
          </div>
        </div>
      </section>

      <section className="spine-summary">
        <div className="summary-stat">
          <div className="summary-label">Effective draw weight</div>
          <div className="summary-value">{effDw.toFixed(1)} lb</div>
          <div className="summary-detail">Includes arrow length + front mass + extended-insert stiffening</div>
        </div>
        <div className="summary-stat">
          <div className="summary-label">Recommended spine range</div>
          <div className="summary-value">{headerRange}</div>
          <div className="summary-detail">±25 tolerance buffer applied</div>
        </div>
        {idealRow && (
          <div className="summary-stat">
            <div className="summary-label">Best in-range spine</div>
            <div className="summary-value">{idealRow.spine}</div>
            <div className="summary-detail">{idealRow.verdict.replace('-', ' ')}</div>
          </div>
        )}
      </section>

      {arrow.frontComponent !== 'standard' && (
        <section className="component-effect">
          <h3>Effect of {componentLabel(arrow.frontComponent)}</h3>
          <p>
            {componentExplanation(arrow.frontComponent)} Calibrated against{' '}
            <a href="/pages/methods#ashby" target="_blank" rel="noopener">
              published manufacturer specs and Stu Miller's dynamic-spine math
            </a>
            . <strong>Sparrow Forge is the only commercial spine recommender that
            models partial-shaft stiffening for these inserts</strong> —
            traditional calculators treat all inserts as mass-only.
          </p>
        </section>
      )}

      <section className="how-we-differ">
        <h3>How this spine math compares</h3>
        <div className="differ-grid">
          <div className="differ-cell">
            <div className="differ-source">Stu Miller's dynamic spine calculator</div>
            <div className="differ-behavior">Models HIT-depth stiffening (1-2″). Still the gold standard for compound + traditional spine math since 2009.</div>
          </div>
          <div className="differ-cell">
            <div className="differ-source">OnTarget!2, Easton selector, Fletched, PCA</div>
            <div className="differ-behavior">Treat ALL inserts as point-mass only. No stiffening model for any extended insert system.</div>
          </div>
          <div className="differ-cell highlight">
            <div className="differ-source">Sparrow Forge (this page)</div>
            <div className="differ-behavior">Extends Stu Miller's working-length reduction to <strong>Firenock CTI 3″/6″, Iron Will Snyder Core (3″), Evolution Outdoors deep glue-ins, and Easton Deep Glue-in (DG)</strong>. The first commercial calculator to do this.</div>
          </div>
        </div>
        <p className="differ-note">
          Core spine math (length, point weight, bow speed) is calibrated against Easton's 301055-A chart and Victory's own spine grids — see the Methods page. The <em>extended-insert stiffening</em> constants above are separate: `EFF_DW_PER_EXTRA_INCH = 4.0` (per inch of embedded footer beyond a standard 1″ HIT) matches Stu Miller's HIT behavior, and `STIFFENING_DAMPING = 0.33` anchors against Ethics Archery's published claim that a 3″ embedded footer yields ~1 spine class shift. The stiffening constants will be tightened against real bareshaft test data as we collect it.
        </p>
      </section>

      <section className="spine-matrix">
        <h3>Full spine ladder</h3>
        <table>
          <thead>
            <tr>
              <th>Spine</th>
              <th>Verdict</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {matrix.map((row) => (
              <SpineRow key={row.spine} row={row} isSelected={row.spine === arrow.selectedSpine} />
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function componentLabel(c: FrontComponentKind): string {
  return {
    'standard': 'Standard insert',
    'iron-will-snyder': 'Iron Will Snyder Core',
    'firenock-cti-3': 'Firenock CTI 3″',
    'firenock-cti-6': 'Firenock CTI 6″',
    'evolution-glue-in': 'Evolution Outdoors deep glue-in',
    'easton-dg': 'Easton Deep Glue-in (DG)',
  }[c];
}

function componentExplanation(c: FrontComponentKind): string {
  switch (c) {
    case 'iron-will-snyder':
      return 'The Snyder Core sits 3″ inside the shaft (vs 1″ for standard HIT). The extra 2″ of metal acts as internal reinforcement, shortening the working length that flexes during the archer\'s paradox. Forge applies a ~8 lb effective draw-weight stiffening offset on top of the added mass.';
    case 'firenock-cti-3':
      return 'The CTI 3″ adds ~25gr of front mass AND stiffens the front portion of the shaft. Forge models both effects: working-length reduction (cubic curve, ~13 spine units stiffer at typical hunting arrow lengths) plus mass-induced weakening. Net is usually slightly stiffer than the baseline spine.';
    case 'firenock-cti-6':
      return 'The CTI 6″ is the maximum-coverage variant. It adds ~46gr and reinforces 6″ of the shaft front. Mass and stiffening both stronger than CTI 3″ — net effect depends on shaft length. On a 28″ arrow the CTI 6″ comes out net slightly weaker; on a 32″ arrow it comes out net stiffer.';
    case 'evolution-glue-in':
      return 'Evolution Outdoors deep glue-in points have an extended shank (~1.5″ estimated; manufacturer doesn\'t publish exact insertion depth). Forge applies a mild stiffening offset and the published mass. Confidence is lower than for Iron Will / Firenock until Evolution publishes specs.';
    case 'easton-dg':
      return 'Easton Deep Glue-in (DG) bodies sit ~2″ inside the shaft. Forge applies a 4 lb effective-DW stiffening offset on top of mass. Stu Miller\'s dynamic-spine calculator uses the same approach.';
    case 'standard':
    default:
      return '';
  }
}

function SpineRow({ row, isSelected }: { row: SpineMatrixRow; isSelected: boolean }) {
  const verdictLabel: Record<SpineMatrixRow['verdict'], string> = {
    'on': 'In range',
    'slightly-stiff': 'Borderline stiff',
    'slightly-weak': 'Borderline weak',
    'too-stiff': 'Too stiff',
    'too-weak': 'Too weak',
  };
  return (
    <tr className={`verdict-${row.verdict} ${isSelected ? 'selected' : ''}`}>
      <td>
        <strong>{row.spine}</strong>
        {isSelected && <span className="current-pill"> current</span>}
      </td>
      <td>
        <span className={`verdict-pill verdict-pill-${row.verdict}`}>
          {verdictLabel[row.verdict]}
        </span>
      </td>
      <td className="row-message">{row.message}</td>
    </tr>
  );
}
